// Calculo de la ruta optima (OSRM) y carga de Leaflet, compartidos por la pagina del pasajero
// (App.js) y el mapa a pantalla completa del ticket (Recorrido.js).

export const ROUTE_BLUE = '#1E88E5';
export const OPT_ORANGE = '#FF6D00';

// Espera el script Y la hoja de estilos: si el mapa se arma antes de que cargue el CSS,
// Leaflet calcula mal el tamano y los mosaicos quedan desacomodados.
export function loadLeafletCDN(onReady) {
  const CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
  let pendientes = 0;
  const listo = () => { if (--pendientes <= 0) onReady(); };
  if (!document.querySelector(`link[href="${CSS}"]`)) {
    pendientes++;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = CSS;
    link.onload = listo;
    link.onerror = listo;
    document.head.appendChild(link);
  }
  if (!window.L) {
    pendientes++;
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = listo;
    document.head.appendChild(script);
  }
  if (pendientes === 0) onReady();
}

export function haversineM(a, b) {
  const R = 6371000;
  const dLat = (b[0] - a[0]) * Math.PI / 180;
  const dLng = (b[1] - a[1]) * Math.PI / 180;
  const x = Math.sin(dLat / 2) ** 2
    + Math.cos(a[0] * Math.PI / 180) * Math.cos(b[0] * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

export function segmentPathLength(positions, fromIdx, toIdx) {
  let total = 0;
  for (let i = fromIdx; i < toIdx; i++) total += haversineM(positions[i], positions[i + 1]);
  return total;
}

export function osrmUrl(from, to) {
  return `https://router.project-osrm.org/route/v1/driving/${from[1]},${from[0]};${to[1]},${to[0]}?overview=full&geometries=geojson`;
}

async function tramoOptimo(from, to) {
  try {
    const data = await fetch(osrmUrl(from, to)).then((r) => r.json());
    if (!data.routes || !data.routes[0]) return null;
    const r = data.routes[0];
    return { coords: r.geometry.coordinates.map((c) => [c[1], c[0]]), dist: r.distance };
  } catch (_) { return null; }
}

/**
 * Ruta optima para comparar con la real. Si el viaje es "circular" (vuelve cerca del inicio),
 * se parte en ida y vuelta por el punto mas lejano. Devuelve { tramos: [[lat,lng]...][], dist } o null.
 */
export async function rutaOptima(positions, realDistM) {
  if (!positions || positions.length < 2 || !(realDistM > 0)) return null;
  const p0 = positions[0];
  const pN = positions[positions.length - 1];
  const lineal = haversineM(p0, pN);
  const circular = lineal < 300 || lineal / realDistM < 0.25;
  let tramos = [];
  if (!circular) {
    const t = await tramoOptimo(p0, pN);
    if (t) tramos = [t];
  } else {
    let maxD = 0, maxIdx = 0;
    for (let i = 1; i < positions.length; i++) {
      const d = haversineM(p0, positions[i]);
      if (d > maxD) { maxD = d; maxIdx = i; }
    }
    const pMax = positions[maxIdx];
    const segs = [];
    if (segmentPathLength(positions, 0, maxIdx) > 200 && haversineM(p0, pMax) > 200) segs.push([p0, pMax]);
    if (segmentPathLength(positions, maxIdx, positions.length - 1) > 200 && haversineM(pMax, pN) > 100) segs.push([pMax, pN]);
    tramos = (await Promise.all(segs.map(([a, b]) => tramoOptimo(a, b)))).filter(Boolean);
  }
  if (!tramos.length) return null;
  return { tramos: tramos.map((t) => t.coords), dist: tramos.reduce((s, t) => s + t.dist, 0) };
}
